import { useEffect, useMemo, useState } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { navItems } from "./constants/data";
import { allowedNavKeys, firstAllowedKey } from "./utils/permissions";
import Sidebar        from "./components/Sidebar";
import ProfileModal   from "./components/ProfileModal";
import ForcePasswordModal from "./components/ForcePasswordModal";
import Topbar         from "./components/Topbar";
import GuideHost      from "./components/GuideHost";
import LoginPage      from "./pages/LoginPage";
import Dashboard      from "./pages/Dashboard";
import Staff          from "./pages/Staff";
import Documents      from "./pages/Documents";
import Approvals      from "./pages/Approvals";
import Meeting        from "./pages/Meeting";
import Reports        from "./pages/Reports";
import Settings       from "./pages/Settings";
import Announcements  from "./pages/Announcements";
import ActivityLog    from "./pages/ActivityLog";
import ContractOfficer from "./pages/ContractOfficer";
import Notifications  from "./pages/Notifications";

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Moul&family=Noto+Sans+Khmer:wght@300;400;500;600;700&display=swap');

  @font-face {
    font-family: 'Khmer OS Siemreap';
    src: url('https://fonts.gstatic.com/s/khmerossen/v18/loC2lQp2T8s1mAbHbXIqmBJ3PCNl.woff2') format('woff2');
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Khmer OS Siemreap','Noto Sans Khmer','Khmer',sans-serif; background: #FDF5F0; }
  button, input, select, textarea { font-family: inherit; }
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #C4956A44; border-radius: 3px; }
`;

function getPage(key, setActive) {
  switch (key) {
    case "dashboard":    return <Dashboard onNavigate={setActive} />;
    case "staff":          return <Staff />;
    case "contract":       return <ContractOfficer />;
    case "doc-officers": return <Documents defaultTab="docs" />;
  
    case "doc-types":    return <Documents defaultTab="types" />;
    case "doc-approval": return <Approvals />;
    case "meeting-list": return <Meeting defaultTab="list" />;
    case "meeting-rooms":return <Meeting defaultTab="rooms" />;
    case "settings":              return <Settings defaultTab="departments" />;
    case "setting-departments":  return <Settings defaultTab="departments" />;
    case "setting-dept-managers":return <Settings defaultTab="dept-managers" />;
    case "setting-offices":      return <Settings defaultTab="offices" />;
    case "setting-positions":    return <Settings defaultTab="positions" />;
    case "setting-users":        return <Settings defaultTab="users" />;
    case "setting-roles":        return <Settings defaultTab="roles" />;
    case "announcements":      return <Announcements defaultTab="list" />;
    case "ann-list":           return <Announcements defaultTab="list" />;
    case "ann-recipients":     return <Announcements defaultTab="recipients" />;
    case "notifications": return <Notifications onNavigate={setActive} />;
    case "reports":       return <Reports />;
    case "activity-log":  return <ActivityLog />;
    default:             return <Dashboard />;
  }
}

function MainApp() {
  const { user, loading, logout } = useAuth();
  const [active,      setActive]      = useState("dashboard");
  const [showProfile, setShowProfile] = useState(null);
  const [guideOpen,  setGuideOpen]  = useState(false);

  const allowedKeys = useMemo(
    () => (user ? allowedNavKeys(navItems, user) : new Set()),
    [user]
  );
  const fallbackKey = useMemo(
    () => (user ? firstAllowedKey(navItems, user) : null),
    [user]
  );

  useEffect(() => {
    if (user && fallbackKey && !allowedKeys.has(active)) setActive(fallbackKey);
  }, [user, allowedKeys, fallbackKey, active]);

  if (loading) {
    return (
      <div style={{
        minHeight:"100vh", display:"flex",
        alignItems:"center", justifyContent:"center",
        background:"#3D1A08",
      }}>
        <p style={{ color:"#C4956A", fontSize:14 }}>កំពុងផ្ទុក...</p>
      </div>
    );
  }

  if (!user) return <LoginPage />;

  if (user.mustSetPassword) return <ForcePasswordModal user={user} />;

  return (
    <div style={{
      display:"flex", height:"100vh",
      background:"#FDF5F0", overflow:"hidden",
    }}>
      <Sidebar
        activeNav={active}
        setActiveNav={setActive}
      />
      <div style={{ flex:1, display:"flex",
                    flexDirection:"column", overflow:"hidden" }}>
        <Topbar activeNav={active} user={user} onLogout={logout} onNavigate={setActive} onProfileClick={(tab) => setShowProfile(tab || "view")} onGuide={() => setGuideOpen(true)} />
        <div data-guide-root style={{ flex:1, overflow:"auto", padding:24 }}>
          {fallbackKey === null ? (
            <div style={{
              height:"100%", display:"flex", flexDirection:"column",
              alignItems:"center", justifyContent:"center", gap:8,
            }}>
              <p style={{ fontSize:16, fontWeight:600, color:"#3D1A08" }}>
                គ្មានសិទ្ធិចូលប្រើ
              </p>
              <p style={{ fontSize:13, color:"#8B6F5C" }}>
                គណនីរបស់អ្នកមិនទាន់មានសិទ្ធិប្រើប្រាស់ទំព័រណាមួយទេ។ សូមទាក់ទងអ្នកគ្រប់គ្រង។
              </p>
            </div>
          ) : (
            getPage(allowedKeys.has(active) ? active : fallbackKey, setActive)
          )}
        </div>
      </div>
      {fallbackKey !== null && (
        <GuideHost pageKey={allowedKeys.has(active) ? active : fallbackKey}
                   open={guideOpen} setOpen={setGuideOpen} />
      )}
      {showProfile && (
        <ProfileModal initialTab={showProfile} onClose={() => setShowProfile(null)} />
      )}
    </div>
  );
}

export default function App() {
  return (
    <>
      <style>{GLOBAL_CSS}</style>
      <LanguageProvider>
        <AuthProvider>
          <MainApp />
        </AuthProvider>
      </LanguageProvider>
    </>
  );
}